import {
  formatParisDateTime,
  KLAVIYO_SUBSCRIBER_CATEGORIES,
  KLAVIYO_SUBSCRIBER_CATEGORY,
  KLAVIYO_SYNC_ACTION,
  RECHARGE_FEATURE_MODE,
  RECHARGE_RUN_STATUS,
  type KlaviyoSubscriberCategory,
  type KlaviyoSyncChange,
  type KlaviyoSyncDetail,
} from '@flux/shared';
import { useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';
import { StatusBadge } from '@/components/ui/badge.js';
import { DescriptionItem, DescriptionList } from '@/components/ui/description-list.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import { ApiError } from '@/lib/api/client.js';
import { queryKeys } from '@/lib/query/keys.js';
import { fetchKlaviyoSync } from './api.js';
import { DetailSheet } from './detail-sheet.js';
import { formatCount } from './money.js';

const DETAIL_ROWS = 8;
const POLL_INTERVAL_MS = 2_000;

const COUNT_ROWS: readonly { key: string; label: string }[] = [
  { key: 'subscriptions', label: 'Abonnements actifs' },
  { key: 'subscribers', label: 'Abonnés (emails)' },
  { key: 'legacy', label: KLAVIYO_SUBSCRIBER_CATEGORY.legacy.label },
  { key: 'legacyNew', label: KLAVIYO_SUBSCRIBER_CATEGORY.legacy_new.label },
  { key: 'new', label: KLAVIYO_SUBSCRIBER_CATEGORY.new.label },
  { key: 'missingEmail', label: 'Abonnements sans email (ignorés)' },
  { key: 'tagged', label: 'Profils déjà tagués' },
  { key: 'unchanged', label: 'Déjà à jour' },
  { key: 'set', label: 'Catégories posées' },
  { key: 'unset', label: 'Catégories retirées' },
  { key: 'errors', label: 'Refus de Klaviyo' },
];

function isCategory(value: string | null): value is KlaviyoSubscriberCategory {
  return value !== null && (KLAVIYO_SUBSCRIBER_CATEGORIES as readonly string[]).includes(value);
}

function CategoryValue({ value }: { value: string | null }): JSX.Element {
  if (value === null) {
    return <span className="text-muted-foreground">aucune</span>;
  }
  return isCategory(value) ? (
    <StatusBadge registry={KLAVIYO_SUBSCRIBER_CATEGORY} code={value} />
  ) : (
    <span className="font-mono text-xs" translate="no">
      {value}
    </span>
  );
}

function ChangeRow({ change }: { change: KlaviyoSyncChange }): JSX.Element {
  return (
    <li className="flex flex-col gap-1 py-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="min-w-0 break-all text-sm" translate="no">
          {change.email ?? <span className="text-muted-foreground">Profil sans email</span>}
        </span>
        <StatusBadge registry={KLAVIYO_SYNC_ACTION} code={change.action} />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
        <CategoryValue value={change.from} />
        <span aria-hidden="true">→</span>
        <span className="sr-only">devient</span>
        <CategoryValue value={change.to} />
      </div>
      {change.error === null ? null : <p className="text-xs text-status-red-fg">{`Refusé par Klaviyo : ${change.error}`}</p>}
    </li>
  );
}

function SyncDetailSkeleton(): JSX.Element {
  return (
    <div aria-busy="true" aria-label="Chargement de la synchronisation" className="flex flex-col gap-3">
      {Array.from({ length: DETAIL_ROWS }, (_, index) => (
        <div key={`detail-skeleton-${String(index)}`} className="grid grid-cols-1 gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-6">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-3.5 w-full max-w-56" />
        </div>
      ))}
    </div>
  );
}

function SyncDetailBody({ sync }: { sync: KlaviyoSyncDetail }): JSX.Element {
  const errors = sync.changes.filter((change) => change.error !== null);
  const others = sync.changes.filter((change) => change.error === null);
  return (
    <div className="flex flex-col gap-6">
      {sync.error === null ? null : (
        <p role="alert" className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg">
          {sync.error}
        </p>
      )}
      <DescriptionList>
        <DescriptionItem label="Statut">
          <StatusBadge registry={RECHARGE_RUN_STATUS} code={sync.status} />
        </DescriptionItem>
        <DescriptionItem label="Mode">
          <StatusBadge registry={RECHARGE_FEATURE_MODE} code={sync.mode} />
        </DescriptionItem>
        <DescriptionItem label="Déclenchement">
          {sync.trigger === 'schedule' ? 'Synchronisation de nuit' : `Manuel${sync.startedBy === null ? '' : ` · ${sync.startedBy.name}`}`}
        </DescriptionItem>
        {sync.finishedAt === null ? null : (
          <DescriptionItem label="Terminée le">
            <span className="whitespace-nowrap tabular-nums">{formatParisDateTime(new Date(sync.finishedAt))}</span>
          </DescriptionItem>
        )}
        {COUNT_ROWS.filter((row) => sync.counts[row.key] !== undefined).map((row) => (
          <DescriptionItem key={row.key} label={row.label}>
            <span className="tabular-nums">{formatCount(sync.counts[row.key] ?? 0)}</span>
          </DescriptionItem>
        ))}
      </DescriptionList>

      <section className="flex flex-col gap-2 border-t border-border pt-4" aria-labelledby="klaviyo-sync-changes">
        <h3 id="klaviyo-sync-changes" className="text-sm font-semibold text-foreground">
          {sync.mode === 'simulation' ? 'Profils qui auraient été modifiés' : 'Profils modifiés'}
        </h3>
        {sync.changes.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {sync.status === 'running' ? 'Synchronisation en cours…' : 'Aucun changement : tous les profils étaient à jour.'}
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border">
            {[...errors, ...others].map((change, index) => (
              <ChangeRow key={`${change.action}-${change.profileId ?? change.email ?? ''}-${String(index)}`} change={change} />
            ))}
          </ul>
        )}
        {sync.changesTruncated > 0 ? (
          <p className="text-xs text-muted-foreground">{`… et ${formatCount(sync.changesTruncated)} autres, non conservés`}</p>
        ) : null}
      </section>
    </div>
  );
}

/** Panneau de détail d'une synchronisation Klaviyo (`06` §11), ouvert par `?id=`. */
export function KlaviyoSyncDetailSheet({ id, onClose }: { id: string | null; onClose: () => void }): JSX.Element {
  const query = useQuery({
    queryKey: queryKeys.subscriptions.klaviyoSync(id ?? ''),
    queryFn: () => fetchKlaviyoSync(id ?? ''),
    enabled: id !== null,
    refetchInterval: (current) => (current.state.data?.status === 'running' ? POLL_INTERVAL_MS : false),
  });
  const sync = query.data;
  const error = query.error instanceof ApiError ? query.error : null;

  return (
    <DetailSheet
      open={id !== null}
      onClose={onClose}
      title="Synchronisation Klaviyo"
      {...(sync ? { description: `Lancée le ${formatParisDateTime(new Date(sync.startedAt))}` } : {})}
    >
      {query.isPending ? (
        <SyncDetailSkeleton />
      ) : query.isError ? (
        <ErrorState
          message={error?.message ?? 'Impossible de charger cette synchronisation.'}
          detail={error?.detail}
          {...(error?.status === 404
            ? {}
            : {
                onRetry: () => {
                  void query.refetch();
                },
              })}
        />
      ) : (
        <SyncDetailBody sync={query.data} />
      )}
    </DetailSheet>
  );
}
